/*
 * OIDO hearing assistant for a standard vision clinic. One script for every clinic page.
 *
 * Backs sections/clinic-assistant.liquid, used by one unlisted page per clinic
 * (templates/page.<slug>-assistant.json). The section's `data-clinic-slug` picks the clinic.
 * Contract mirrors apps/erp-api/src/routes/partner/clinic-chat.ts:
 *
 *   POST <base>/v1/partner/clinics/<slug>/session   { password }   -> { token, expiresAt }
 *   POST <base>/v1/partner/clinics/<slug>/chat      { messages }   -> { reply }
 *        (Authorization: Bearer <token>)
 *
 * The access code is NEVER checked here. This file is served publicly from Shopify's
 * CDN, so a client-side comparison would be decoration. erp-api validates the code and
 * returns a short-lived signed token; this only carries that token.
 *
 * Forked from assets/visology-sales.js. The token and history storage keys carry the
 * slug, so one clinic's page never optimistically reuses another clinic's token (erp-api
 * would 401 it anyway: tokens are namespaced per clinic server-side).
 *
 * ES5 on purpose, no build step, no import from the theme's own theme.js. Same shape as
 * assets/warranty-registration.js, which is the proven pattern in this theme.
 */
(function () {
  'use strict';

  var MAX_CHARS = 2000;
  // Capped so a long shift cannot grow sessionStorage without bound.
  var MAX_SESSIONS = 20;
  // Only the tail of a conversation is sent each turn. erp-api reads the last 12 messages and
  // refuses more than 80, so sending the whole thing broke any conversation past about 40
  // questions. 24 keeps a margin over what the server reads.
  var MAX_SENT = 24;

  // Every string this script writes, in English and Spanish, for the page's English /
  // Español toggle. The section renders its own copy in both languages; these are the
  // strings that only exist once the script is running. Usted, as in the Quick Hearing
  // Check's Spanish.
  //
  // `errors`: the server returns a machine-readable `code` and the client owns the wording.
  // Same convention as ERROR_COPY in warranty-registration.js.
  var COPY = {
    en: {
      errors: {
        bad_password: 'That access code is not right. Check it with your OIDO contact.',
        chat_unconfigured:
          'The assistant is not switched on yet. Contact OIDO at help@oidohear.com.',
        corpus_empty:
          'The knowledge base has not been loaded yet. Contact OIDO at help@oidohear.com.',
        rate_limited: 'Message limit reached for now. Try again in a little while.',
        chat_paused: 'The assistant is temporarily unavailable. OIDO has been notified.',
        too_many_attempts: 'Too many attempts. Wait a few minutes and try again.',
        upstream_rate_limited: 'The assistant is busy right now. Try again in a moment.',
        unauthorized: 'Your session expired. Enter the access code again.'
      },
      generic: 'Something went wrong. Try again in a moment.',
      enterCode: 'Enter your access code.',
      misconfigured: 'This page is misconfigured (no API URL or clinic). Contact OIDO.',
      checking: 'Checking...',
      tooLong: 'That message is too long. Shorten it and try again.',
      expired: 'Your session expired. Enter the access code again.',
      thinking: 'Thinking...',
      copy: 'Copy',
      copied: 'Copied',
      copyFailed: 'Copy failed',
      nothingYet: 'Nothing yet.',
      listening: 'Listening. Tap the mic again to stop.',
      micBlocked:
        'The microphone is blocked for this site. Allow it in the browser, or type instead.',
      dictationStopped: function (kind) {
        return 'Dictation stopped (' + kind + '). Type instead.';
      }
    },
    es: {
      errors: {
        bad_password:
          'Ese código de acceso no es correcto. Confírmelo con su contacto de OIDO.',
        chat_unconfigured:
          'El asistente aún no está activado. Comuníquese con OIDO en help@oidohear.com.',
        corpus_empty:
          'La base de conocimientos aún no está cargada. Comuníquese con OIDO en help@oidohear.com.',
        rate_limited: 'Llegó al límite de mensajes por ahora. Inténtelo de nuevo en un rato.',
        chat_paused: 'El asistente no está disponible por el momento. OIDO ya fue notificado.',
        too_many_attempts: 'Demasiados intentos. Espere unos minutos e inténtelo de nuevo.',
        upstream_rate_limited: 'El asistente está ocupado. Inténtelo de nuevo en un momento.',
        unauthorized: 'Su sesión expiró. Vuelva a ingresar el código de acceso.'
      },
      generic: 'Algo salió mal. Inténtelo de nuevo en un momento.',
      enterCode: 'Ingrese su código de acceso.',
      misconfigured: 'Esta página está mal configurada (sin URL de la API o sin óptica). Comuníquese con OIDO.',
      checking: 'Verificando...',
      tooLong: 'Ese mensaje es demasiado largo. Acórtelo e inténtelo de nuevo.',
      expired: 'Su sesión expiró. Vuelva a ingresar el código de acceso.',
      thinking: 'Pensando...',
      copy: 'Copiar',
      copied: 'Copiado',
      copyFailed: 'No se pudo copiar',
      nothingYet: 'Nada todavía.',
      listening: 'Escuchando. Toque el micrófono otra vez para parar.',
      micBlocked:
        'El micrófono está bloqueado para este sitio. Permítalo en el navegador, o escriba.',
      dictationStopped: function (kind) {
        return 'Se detuvo el dictado (' + kind + '). Escriba en su lugar.';
      }
    }
  };

  // Attributes the section cannot render twice. Each element carries data-en-<attr> and
  // data-es-<attr>, and setLang copies the active one in.
  var LANG_ATTRS = ['placeholder', 'aria-label', 'title'];

  function init(root) {
    var apiBase = (root.getAttribute('data-api-base') || '').replace(/\/+$/, '');
    // Served by `shopify theme dev` (npm run dev:storefront, 127.0.0.1:9292), the page talks to
    // the dev erp-api from `npm run dev:erp-api:local` (port 3210), never to production. The live
    // page is served from oidohear.com, so this never applies there.
    if (/^(127\.0\.0\.1|localhost)$/.test(window.location.hostname)) apiBase = 'http://localhost:3210';
    // One page per clinic, all on this one script. The slug picks the clinic's own erp-api
    // router, and namespaces the stored token and history, so two clinics' pages open in
    // one browser never share either.
    var slug = (root.getAttribute('data-clinic-slug') || '').trim();
    var clinicPath = '/v1/partner/clinics/' + encodeURIComponent(slug);
    var SESSION_PATH = clinicPath + '/session';
    var CHAT_PATH = clinicPath + '/chat';
    // localStorage: the token is a DEVICE credential. Staff enter the access code once per
    // device, and it survives new tabs, reloads and a tablet dropping the page from memory,
    // which a tab-scoped token did not (they were re-entering it several times a day). It
    // lasts a year, erp-api hands back a fresh one while the device is in use, and changing
    // the clinic's access code signs every device out.
    var STORAGE_KEY = 'oido-clinic-' + slug + '-token';
    // Transcripts, unlike the token, stay TAB-scoped in sessionStorage: see `sessions` below.
    var HISTORY_KEY = STORAGE_KEY + '-chats';
    // localStorage, like the token: a language is a staff member's preference, not anything
    // sensitive, so it survives the tab and a team member sets it once per device.
    var LANG_KEY = 'oido-clinic-' + slug + '-lang';

    var lang = 'en';
    try {
      if (window.localStorage.getItem(LANG_KEY) === 'es') lang = 'es';
    } catch (e) {
      // Storage disabled. English, and the toggle still works for this page view.
    }

    function t(key) {
      return COPY[lang][key];
    }

    var gate = root.querySelector('[data-oido-assistant-gate]');
    var chat = root.querySelector('[data-oido-assistant-chat]');
    var log = root.querySelector('[data-oido-assistant-log]');
    var form = root.querySelector('[data-oido-assistant-form]');
    var textarea =
      root.querySelector('[data-oido-assistant-textarea]') ||
      root.querySelector('#oido-assistant-message') ||
      root.querySelector('.oido-assistant__textarea');
    var codeInput =
      root.querySelector('[data-oido-assistant-code]') ||
      root.querySelector('#oido-assistant-code') ||
      root.querySelector('input[type="password"]');
    var unlockBtn = root.querySelector('[data-oido-assistant-unlock]');
    var sendBtn = root.querySelector('[data-oido-assistant-send]');
    var statusEl = root.querySelector('[data-oido-assistant-status]');

    // Direction J additions: the empty state and its starter chips. Both optional,
    // so an older/edited template without them still works.
    var emptyEl = root.querySelector('[data-oido-assistant-empty]');
    var chipEls = root.querySelectorAll('[data-oido-assistant-chip]');
    var resetBtn = root.querySelector('[data-oido-assistant-reset]');
    var sideEl = root.querySelector('[data-oido-assistant-side]');
    var sideToggle = root.querySelector('[data-oido-assistant-side-toggle]');
    var historyEl = root.querySelector('[data-oido-assistant-history]');
    var micBtn = root.querySelector('[data-oido-assistant-mic]');
    var langBtns = root.querySelectorAll('[data-oido-assistant-lang]');

    if (!gate || !chat || !log || !form || !textarea || !codeInput) return;

    // --------------------------------------------------------- composer height --
    // The box grows with the draft and drops back to one line on blur, so a long
    // message is never hidden inside a one-line box and the answers above stay
    // readable while composing. Height is written INLINE, which beats every
    // stylesheet rule, so none of this depends on winning the cascade.
    var MAX_H_DESKTOP = 120;
    var MAX_H_PHONE = 96;
    var phoneMq = window.matchMedia('(max-width: 640px)');
    // One-line height, MEASURED rather than hardcoded so the CSS and this file cannot
    // disagree and a single-line draft does not jump on focus. 0 means not yet measured.
    var baseH = 0;

    function maxHeight() {
      return phoneMq.matches ? MAX_H_PHONE : MAX_H_DESKTOP;
    }

    function measureBase() {
      // offsetParent is null while the section sits behind the access-code gate, where
      // every measurement is 0. Stay unmeasured and retry once the chat is shown.
      if (baseH || textarea.offsetParent === null) return baseH;
      // The PLACEHOLDER is what an empty textarea measures, and this one wraps to two
      // lines on a phone, so blanking the value alone measures the placeholder rather
      // than one line of text and the collapsed box comes back double height.
      var draft = textarea.value;
      var hint = textarea.placeholder;
      textarea.value = '';
      textarea.placeholder = '';
      textarea.style.height = 'auto';
      baseH = textarea.scrollHeight;
      textarea.value = draft;
      textarea.placeholder = hint;
      return baseH;
    }

    function fitComposer() {
      measureBase();
      var cap = maxHeight();
      textarea.style.height = 'auto';
      // Same placeholder trap: an empty box would otherwise sit at placeholder height
      // instead of one line, so an empty composer uses the measured baseline directly.
      var wanted = textarea.value === '' && baseH ? baseH : textarea.scrollHeight;
      var next = Math.min(wanted, cap);
      textarea.style.height = next + 'px';
      textarea.style.overflowY = wanted > cap ? 'auto' : 'hidden';
      if (baseH > 0 && next > baseH + 1) form.setAttribute('data-grown', '');
      else form.removeAttribute('data-grown');
    }

    function collapseComposer() {
      var base = measureBase();
      textarea.style.height = base ? base + 'px' : '';
      textarea.style.overflowY = 'hidden';
      // Back to the top of the draft. The grown box was scrolled to the caret, and
      // leaving it there collapses onto a half-clipped middle line, which is the
      // "hides text" look this change exists to remove. Focus scrolls back to the
      // caret on its own.
      textarea.scrollTop = 0;
      form.removeAttribute('data-grown');
    }

    textarea.addEventListener('input', fitComposer);
    // Focus regrows to fit whatever draft is already there, blur drops back to one
    // line: tap out to read the answer in full, tap back in and carry on typing.
    textarea.addEventListener('focus', fitComposer);
    textarea.addEventListener('blur', collapseComposer);

    // The phone rules change the font size as well as the cap, so a baseH measured on
    // one side of the breakpoint is wrong on the other, and rotating a phone with a
    // draft in the box would clip the COLLAPSED state, which is this whole bug in
    // miniature. Drop the measurement and take it again at the new size. Guarded
    // because MediaQueryList.addEventListener landed in Safari 14, and a throw here
    // would abort the rest of setup and take the chat with it.
    if (typeof phoneMq.addEventListener === 'function') {
      phoneMq.addEventListener('change', function () {
        baseH = 0;
        if (document.activeElement === textarea) fitComposer();
        else collapseComposer();
      });
    }

    // Conversation history. Its tail is sent each turn (the Messages API is stateless; see
    // MAX_SENT). This is what the page remembers.
    var history = [];
    var token = null;
    var busy = false;
    // Assigned by setupDictation only where the browser has the Web Speech API,
    // so every caller must null-check it.
    var stopDictation = null;

    // Past conversations from THIS TAB. sessionStorage, deliberately NOT localStorage like the
    // token: a shared shop tablet must not hand the next person the last person's
    // transcripts, and staff paste customer details into these, so nothing should outlive
    // the tab. Only the credential is remembered per device, never the content. There is no
    // server side to this at all, because the chat API is stateless and the client
    // already owns the whole history it sends on every turn.
    var sessions = [];
    var currentId = null;

    try {
      var storedSessions = JSON.parse(window.sessionStorage.getItem(HISTORY_KEY) || '[]');
      if (Object.prototype.toString.call(storedSessions) === '[object Array]') {
        sessions = storedSessions;
      }
    } catch (e) {
      // Private mode, storage disabled, or a value in an older shape. Start clean.
      sessions = [];
    }

    try {
      token = window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      // Private mode / storage disabled. Not fatal: the user just re-enters the code.
      token = null;
    }

    function setStatus(message, kind) {
      if (!statusEl) return;
      statusEl.textContent = message;
      statusEl.setAttribute('data-kind', kind || 'info');
      statusEl.hidden = false;
    }

    function clearStatus() {
      if (!statusEl) return;
      statusEl.textContent = '';
      statusEl.removeAttribute('data-kind');
      statusEl.hidden = true;
    }

    function storeToken(value) {
      token = value;
      try {
        if (value) window.localStorage.setItem(STORAGE_KEY, value);
        else window.localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        // Ignore: the in-memory `token` still works for this page view.
      }
    }

    function showChat() {
      gate.hidden = true;
      chat.hidden = false;
      clearStatus();
      textarea.focus();
    }

    function showGate(message) {
      if (stopDictation) stopDictation();
      chat.hidden = true;
      gate.hidden = false;
      storeToken(null);
      history = [];
      // The rail goes with it. Returning to the gate means this session is no longer
      // trusted, and these transcripts carry whatever a staff member pasted in.
      sessions = [];
      currentId = null;
      try {
        window.sessionStorage.removeItem(HISTORY_KEY);
      } catch (e) {
        // Nothing to clear if storage is unavailable.
      }
      renderHistory();
      log.innerHTML = '';
      chat.removeAttribute('data-oido-assistant-started');
      if (emptyEl) emptyEl.hidden = false;
      if (message) setStatus(message, 'error');
      codeInput.value = '';
      codeInput.focus();
    }

    function escapeHtml(text) {
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    // The model answers in light markdown, and rendering it with textContent left
    // literal ** all over the replies. This handles what it actually emits: bold, inline
    // `code`, and fenced ``` blocks.
    //
    // The fenced block is NEW in this fork, and it is load-bearing: it is how the
    // escalation email arrives, and the corpus promises the team
    // member one block they copy in one action, To line and subject included. The
    // Visology scripts handle inline code only, so a fence there renders as stray
    // backticks around a stack of separately highlighted lines, with nothing to copy.
    //
    // Order matters and is the whole safety argument: every segment is HTML-escaped
    // FIRST, so the only tags that can reach innerHTML are the ones added here.
    // Never move the escape below the replacements.
    function renderInline(text) {
      return escapeHtml(text)
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/`([^`]+)`/g, '<code>$1</code>');
    }

    var FENCE = /```[^\n]*\n([\s\S]*?)```/g;

    function renderMarkdown(el, text) {
      var html = '';
      var last = 0;
      var match;
      FENCE.lastIndex = 0;
      while ((match = FENCE.exec(text)) !== null) {
        // The block is its own line, so the newlines either side of it would only add
        // empty lines under pre-wrap.
        html += renderInline(text.slice(last, match.index).replace(/\n+$/, ''));
        html +=
          '<div class="oido-assistant__block">' +
          '<pre class="oido-assistant__pre">' +
          escapeHtml(match[1].replace(/\n$/, '')) +
          '</pre>' +
          '<button class="oido-assistant__copy" type="button" data-oido-assistant-copy>' +
          escapeHtml(t('copy')) +
          '</button></div>';
        last = FENCE.lastIndex;
        while (text.charAt(last) === '\n') last++;
        FENCE.lastIndex = last;
      }
      html += renderInline(text.slice(last));
      el.innerHTML = html;
    }

    // The empty state (prompt + chips) owns the pane until the first message. CSS does
    // the swap off this flag, so nothing depends on JS for the arrival state to look right.
    function markStarted() {
      if (chat.hasAttribute('data-oido-assistant-started')) return;
      chat.setAttribute('data-oido-assistant-started', '');
      if (emptyEl) emptyEl.hidden = true;
    }

    function saveSessions() {
      try {
        window.sessionStorage.setItem(HISTORY_KEY, JSON.stringify(sessions));
      } catch (e) {
        // Out of quota or storage disabled. The rail still works for this page view.
      }
    }

    function titleFrom(text) {
      var t = String(text).replace(/\s+/g, ' ').trim();
      return t.length > 44 ? t.slice(0, 43) + '\u2026' : t;
    }

    function findSession(id) {
      for (var i = 0; i < sessions.length; i++) {
        if (sessions[i].id === id) return sessions[i];
      }
      return null;
    }

    function renderHistory() {
      if (!historyEl) return;
      historyEl.innerHTML = '';

      if (!sessions.length) {
        var empty = document.createElement('li');
        empty.className = 'oido-assistant__side-empty';
        empty.textContent = t('nothingYet');
        historyEl.appendChild(empty);
        return;
      }

      for (var i = 0; i < sessions.length; i++) {
        historyEl.appendChild(historyItem(sessions[i]));
      }
    }

    // Built in its own function so the click handler closes over one session, not over
    // a loop variable that has moved on by the time anyone taps it.
    function historyItem(session) {
      var li = document.createElement('li');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'oido-assistant__side-item';
      // textContent, never innerHTML: the title is a staff member's own typing.
      btn.textContent = session.title;
      btn.title = session.title;
      if (session.id === currentId) btn.setAttribute('aria-current', 'true');
      btn.addEventListener('click', function () {
        openSession(session.id);
      });
      li.appendChild(btn);
      return li;
    }

    // Called after every completed turn, so a conversation reaches the rail as soon as
    // it exists rather than only when someone remembers to start a new one.
    function persistCurrent() {
      if (!history.length) return;
      var session = findSession(currentId);
      if (!session) {
        session = {
          id: String(Date.now()) + String(Math.floor(Math.random() * 1000)),
          title: titleFrom(history[0].content),
          messages: []
        };
        currentId = session.id;
        sessions.unshift(session);
        while (sessions.length > MAX_SESSIONS) sessions.pop();
      }
      session.messages = history.slice();
      saveSessions();
      renderHistory();
    }

    function openSession(id) {
      if (busy) return;
      if (stopDictation) stopDictation();
      var session = findSession(id);
      if (!session) return;

      currentId = session.id;
      history = session.messages.slice();
      log.innerHTML = '';
      chat.removeAttribute('data-oido-assistant-started');
      if (emptyEl) emptyEl.hidden = false;
      for (var i = 0; i < history.length; i++) {
        appendMessage(history[i].role, history[i].content);
      }
      clearStatus();
      renderHistory();
      closeSide();
      textarea.focus();
    }

    // Start a fresh case: drop the transcript and the history the server is sent, but
    // KEEP the session token. Staff are mid-shift on a shared tablet, and making them
    // re-enter the access code just to move to the next customer is friction with no
    // security value, since the device already holds a valid token either way. The chat
    // they were in is already in the rail, so starting over loses nothing.
    function startNewChat() {
      if (busy) return;
      if (stopDictation) stopDictation();
      currentId = null;
      history = [];
      log.innerHTML = '';
      chat.removeAttribute('data-oido-assistant-started');
      if (emptyEl) emptyEl.hidden = false;
      clearStatus();
      textarea.value = '';
      collapseComposer();
      renderHistory();
      closeSide();
      textarea.focus();
    }

    function closeSide() {
      chat.removeAttribute('data-oido-assistant-side-open');
    }

    function appendMessage(role, text) {
      markStarted();
      var el = document.createElement('div');
      el.className =
        'oido-assistant__msg oido-assistant__msg--' + (role === 'user' ? 'user' : 'bot');
      // A staff member's own words are never treated as markup.
      if (role === 'user') el.textContent = text;
      else renderMarkdown(el, text);
      log.appendChild(el);
      log.scrollTop = log.scrollHeight;
      return el;
    }

    function setBusy(value) {
      busy = value;
      if (sendBtn) {
        sendBtn.disabled = value;
        sendBtn.setAttribute('aria-busy', value ? 'true' : 'false');
      }
      textarea.disabled = value;
      if (micBtn) micBtn.disabled = value;
    }

    // Turn a fetch Response into a rejected promise carrying the server's coded copy.
    function toError(res) {
      return res.json().then(
        function (body) {
          var code = body && body.error;
          var msg = (code && t('errors')[code]) || (body && body.message) || t('generic');
          var err = new Error(msg);
          err.code = code;
          err.status = res.status;
          return Promise.reject(err);
        },
        function () {
          var err = new Error(t('generic'));
          err.status = res.status;
          return Promise.reject(err);
        }
      );
    }

    function unlock(password) {
      return fetch(apiBase + SESSION_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: password })
      }).then(function (res) {
        if (!res.ok) return toError(res);
        return res.json();
      });
    }

    function ask(messages) {
      return fetch(apiBase + CHAT_PATH, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token
        },
        // erp-api answers in this language. Read at send time, so switching mid-conversation
        // changes the next reply and leaves the earlier ones as they were.
        body: JSON.stringify({ messages: messages.slice(-MAX_SENT), lang: lang })
      }).then(function (res) {
        if (!res.ok) return toError(res);
        return res.json();
      });
    }

    gate.addEventListener('submit', function (event) {
      event.preventDefault();
      if (busy) return;

      var password = codeInput.value.trim();
      if (!password) {
        setStatus(t('enterCode'), 'error');
        return;
      }
      if (!apiBase || !slug) {
        setStatus(t('misconfigured'), 'error');
        return;
      }

      setBusy(true);
      if (unlockBtn) unlockBtn.disabled = true;
      setStatus(t('checking'), 'info');

      unlock(password)
        .then(function (body) {
          storeToken(body.token);
          showChat();
        })
        .catch(function (err) {
          setStatus(err.message, 'error');
        })
        .then(function () {
          setBusy(false);
          if (unlockBtn) unlockBtn.disabled = false;
        });
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (busy) return;
      if (stopDictation) stopDictation();

      var text = textarea.value.trim();
      if (!text) return;
      if (text.length > MAX_CHARS) {
        setStatus(t('tooLong'), 'error');
        return;
      }
      if (!token) {
        showGate(t('expired'));
        return;
      }

      clearStatus();
      appendMessage('user', text);
      history.push({ role: 'user', content: text });
      textarea.value = '';
      collapseComposer();
      setBusy(true);

      var pending = appendMessage('assistant', t('thinking'));
      pending.className += ' oido-assistant__msg--pending';

      ask(history)
        .then(function (body) {
          // erp-api renews a device's token while it is in use; keep whatever comes back.
          if (body.token) storeToken(body.token);
          pending.className = 'oido-assistant__msg oido-assistant__msg--bot';
          renderMarkdown(pending, body.reply);
          history.push({ role: 'assistant', content: body.reply });
          persistCurrent();
          log.scrollTop = log.scrollHeight;
        })
        .catch(function (err) {
          if (pending.parentNode) pending.parentNode.removeChild(pending);
          // Drop the unanswered turn so a retry does not resend a duplicate user message.
          history.pop();
          if (err.status === 401) {
            showGate(err.message);
            return;
          }
          setStatus(err.message, 'error');
        })
        .then(function () {
          setBusy(false);
          textarea.focus();
        });
    });

    // A chip is just a pre-written question: fill the composer and submit, so the chip
    // path and the typed path stay one code path and cannot drift apart.
    Array.prototype.forEach.call(chipEls, function (chip) {
      chip.addEventListener('click', function () {
        if (busy) return;
        textarea.value = chip.getAttribute('data-oido-assistant-chip') || chip.textContent.trim();
        fitComposer();
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { cancelable: true }));
      });
    });

    if (resetBtn) {
      resetBtn.addEventListener('click', startNewChat);
    }

    if (sideToggle) {
      sideToggle.addEventListener('click', function () {
        if (chat.hasAttribute('data-oido-assistant-side-open')) closeSide();
        else chat.setAttribute('data-oido-assistant-side-open', '');
      });
    }

    // Copy for a fenced block. Delegated on the log, because the transcript is rebuilt
    // from history whenever a past conversation is reopened from the rail.
    log.addEventListener('click', function (event) {
      var btn = event.target.closest && event.target.closest('[data-oido-assistant-copy]');
      if (!btn) return;
      var pre = btn.parentNode.querySelector('pre');
      if (!pre) return;
      function label(text) {
        btn.textContent = text;
        window.setTimeout(function () {
          btn.textContent = t('copy');
        }, 1600);
      }
      try {
        navigator.clipboard.writeText(pre.textContent).then(
          function () {
            label(t('copied'));
          },
          function () {
            label(t('copyFailed'));
          }
        );
      } catch (e) {
        label(t('copyFailed'));
      }
    });

    // Tapping the transcript behind an open drawer closes it, which is what the scrim
    // looks like it should do.
    chat.addEventListener('click', function (event) {
      if (!chat.hasAttribute('data-oido-assistant-side-open')) return;
      if (sideEl && sideEl.contains(event.target)) return;
      closeSide();
    });

    // ------------------------------------------------------------- dictation --
    // Speech to text via the Web Speech API. Every browser this is used from has it
    // except Firefox: Chrome and Edge on a counter PC, Safari on a phone. No key, no
    // dependency, no backend, and no audio reaches an OIDO server.
    //
    // Stated plainly, because staff will speak customer details into this: the
    // recognition itself is done by the BROWSER VENDOR (Google for Chrome and Edge,
    // Apple for Safari), so the audio leaves the device, exactly as the phone
    // keyboard's own dictation button already sends it today. It is opt-in per tap and
    // is never open unless the button is lit.
    if (micBtn) setupDictation();

    function setupDictation() {
      var Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      // Firefox, or anything older. Leave the button hidden: a control that is present
      // and does nothing is worse than no control.
      if (!Recognition) return;

      // One dictation can span several recognition sessions, because Safari ends a
      // session on every pause. This caps the automatic restarts, so a service that
      // has broken, or a permission revoked mid-session, cannot spin forever.
      var MAX_RESTARTS = 40;

      var recognition = new Recognition();
      recognition.continuous = true;
      recognition.interimResults = true;

      var listening = false; // what the user asked for
      var running = false; // whether a recognition session is actually open
      var fatal = false; // set by onerror, which fires BEFORE onend
      var restarts = 0;
      var committed = ''; // the box's text before this session's words

      function paint() {
        micBtn.setAttribute('aria-pressed', listening ? 'true' : 'false');
        if (listening) micBtn.setAttribute('data-listening', '');
        else micBtn.removeAttribute('data-listening');
      }

      // Join what is already in the box to what was just heard: no two words glued
      // together, and no leading space in an empty box.
      function joined(base, heard) {
        var head = base.replace(/\s+$/, '');
        var tail = heard.replace(/^\s+/, '');
        if (!head) return tail;
        if (!tail) return head;
        return head + ' ' + tail;
      }

      function start() {
        if (running) return;
        // The page's language, not <html lang>, which the theme always sets to English: a
        // Spanish question transcribed by an English recognizer comes out as nonsense.
        // setLang stops dictation first, so a session never changes language mid-way.
        recognition.lang = lang === 'es' ? 'es-US' : 'en-US';
        try {
          recognition.start();
          running = true;
        } catch (err) {
          // start() throws if the previous session has not finished tearing down. One
          // retry on a later tick, then give up quietly rather than looping.
          window.setTimeout(function () {
            if (!listening || running) return;
            try {
              recognition.start();
              running = true;
            } catch (e) {
              stop();
            }
          }, 200);
        }
      }

      function stop() {
        var wasListening = listening;
        listening = false;
        restarts = 0;
        paint();
        if (running) {
          try {
            recognition.stop();
          } catch (e) {
            // Already stopped. Nothing to do.
          }
        }
        // Clear our own message only, never an error that has just landed.
        if (wasListening && statusEl && statusEl.textContent === t('listening')) clearStatus();
      }

      recognition.onresult = function (event) {
        // A result can still arrive after stop() and before onend. By then the composer
        // may have been cleared or switched to another conversation, so drop it.
        if (!listening) return;
        // Rebuilt from index 0 every time. With `continuous` the results are cumulative
        // for the session, so reading only from resultIndex double-counts finals on
        // some browsers and drops them on others.
        var finalText = '';
        var interim = '';
        for (var i = 0; i < event.results.length; i++) {
          var result = event.results[i];
          if (!result || !result[0]) continue;
          if (result.isFinal) finalText += result[0].transcript;
          else interim += result[0].transcript;
        }
        var next = joined(committed, finalText + interim);
        // maxlength does not apply to a value set from script, so cap it here or a long
        // dictation sends a message the server will reject.
        textarea.value = next.length > MAX_CHARS ? next.slice(0, MAX_CHARS) : next;
        fitComposer();
      };

      recognition.onerror = function (event) {
        var kind = event && event.error;
        // Routine, not a failure: nobody has spoken yet, or we stopped it ourselves.
        if (kind === 'no-speech' || kind === 'aborted') return;
        fatal = true;
        if (kind === 'not-allowed' || kind === 'service-not-allowed') {
          setStatus(t('micBlocked'), 'error');
        } else {
          setStatus(t('dictationStopped')(kind || 'unknown error'), 'error');
        }
      };

      recognition.onend = function () {
        running = false;
        // Fold this session's words into the base so the next session appends instead
        // of overwriting. Safari ends a session on every pause, so this runs a lot.
        committed = textarea.value;
        if (!listening || fatal || restarts >= MAX_RESTARTS) {
          stop();
          return;
        }
        restarts++;
        start();
      };

      micBtn.addEventListener('click', function () {
        if (listening) {
          stop();
          return;
        }
        if (busy) return;
        fatal = false;
        restarts = 0;
        committed = textarea.value;
        listening = true;
        paint();
        setStatus(t('listening'), 'info');
        start();
      });

      // Only now does the control do something, so only now is it shown.
      micBtn.hidden = false;
      stopDictation = stop;
    }

    // ------------------------------------------------------- English / Español --
    // The section renders its copy in both languages and CSS shows the root's
    // data-lang. This switches that, copies the language's attribute values in, relabels
    // what the script itself wrote, and remembers the choice for this device. The next
    // chat request carries it, so the assistant answers in it.
    function setLang(next, persist) {
      if (next !== 'en' && next !== 'es') return;
      // Before the switch, so stop() still recognizes its own message in the old language.
      if (stopDictation) stopDictation();
      lang = next;
      root.setAttribute('data-lang', lang);
      root.setAttribute('lang', lang);

      for (var a = 0; a < LANG_ATTRS.length; a++) {
        var source = 'data-' + lang + '-' + LANG_ATTRS[a];
        var els = root.querySelectorAll('[' + source + ']');
        for (var i = 0; i < els.length; i++) {
          els[i].setAttribute(LANG_ATTRS[a], els[i].getAttribute(source));
        }
      }

      var copies = log.querySelectorAll('[data-oido-assistant-copy]');
      for (var c = 0; c < copies.length; c++) copies[c].textContent = t('copy');

      for (var b = 0; b < langBtns.length; b++) {
        var on = langBtns[b].getAttribute('data-oido-assistant-lang') === lang;
        langBtns[b].setAttribute('aria-pressed', on ? 'true' : 'false');
      }

      renderHistory();
      // A status line would now be in the other language. Every one of them is either
      // transient or repeated by the next action, so dropping it loses nothing.
      clearStatus();

      if (persist) {
        try {
          window.localStorage.setItem(LANG_KEY, lang);
        } catch (e) {
          // Storage disabled. The choice still holds for this page view.
        }
      }
    }

    Array.prototype.forEach.call(langBtns, function (btn) {
      btn.addEventListener('click', function () {
        setLang(btn.getAttribute('data-oido-assistant-lang'), true);
      });
    });

    // Also draws the history rail for the first time.
    setLang(lang, false);

    // Enter sends, Shift+Enter makes a newline. Staff are typing one-line questions
    // with a customer waiting, so reaching for the button every time is friction.
    textarea.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { cancelable: true }));
      }
    });

    // A stored token may already be expired; the first chat call returns 401 and drops
    // back to the gate. Showing the chat optimistically avoids a pointless re-entry
    // every time someone opens or reloads the page.
    if (token) showChat();
  }

  function boot() {
    var roots = document.querySelectorAll('[data-oido-assistant]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
